import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CreatureTrait } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

interface SelectCreatureTraitModalProps {
  open: boolean;
  excludeTraitNames: string[];
  onClose: () => void;
  onSelect: (trait: CreatureTrait) => void;
}

export const SelectCreatureTraitModal: React.FC<SelectCreatureTraitModalProps> = ({
  open,
  excludeTraitNames,
  onClose,
  onSelect,
}) => {
  const creatures = useReferenceDataStore((s) => s.creatures);

  const existingTraits = React.useMemo<CreatureTrait[]>(() => {
    const seen = new Map<string, CreatureTrait>();
    for (const c of creatures) {
      for (const t of c.traits) {
        const key = t.name.trim().toLowerCase();
        if (!seen.has(key)) seen.set(key, t);
      }
    }
    const excludeLower = new Set(excludeTraitNames.map((n) => n.trim().toLowerCase()));
    return Array.from(seen.values())
      .filter((t) => !excludeLower.has(t.name.trim().toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [creatures, excludeTraitNames]);

  const [selectedExisting, setSelectedExisting] = React.useState<CreatureTrait | null>(null);
  const [newName, setNewName] = React.useState('');
  const [newDescription, setNewDescription] = React.useState('');
  const [nameError, setNameError] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setSelectedExisting(null);
      setNewName('');
      setNewDescription('');
      setNameError('');
    }
  }, [open]);

  const handlePickExisting = (trait: CreatureTrait) => {
    setSelectedExisting(trait);
    // Clear the new-trait form when picking from the list
    setNewName('');
    setNewDescription('');
    setNameError('');
  };

  const handleNewNameChange = (value: string) => {
    setNewName(value);
    setNameError('');
    // Clear existing selection when the user starts typing a new trait
    if (value) setSelectedExisting(null);
  };

  const canConfirm = selectedExisting !== null || newName.trim() !== '';

  const handleConfirm = () => {
    if (selectedExisting) {
      onSelect({ ...selectedExisting, id: crypto.randomUUID() });
      return;
    }
    if (!newName.trim()) {
      setNameError('Trait name is required');
      return;
    }
    onSelect({
      id: crypto.randomUUID(),
      name: newName.trim(),
      description: newDescription.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Trait</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {existingTraits.length > 0 && (
          <>
            <Typography variant="subtitle2">Existing Traits</Typography>
            <List disablePadding sx={{ maxHeight: 240, overflowY: 'auto' }}>
              {existingTraits.map((trait) => (
                <ListItemButton
                  key={trait.id}
                  selected={selectedExisting?.id === trait.id}
                  onClick={() => handlePickExisting(trait)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText primary={trait.name} secondary={trait.description} />
                </ListItemButton>
              ))}
            </List>
            <Divider />
          </>
        )}

        <Typography variant="subtitle2">New Trait</Typography>
        <Stack spacing={1.5}>
          <TextField
            label="Trait Name"
            value={newName}
            onChange={(e) => handleNewNameChange(e.target.value)}
            error={!!nameError}
            helperText={nameError}
            size="small"
            fullWidth
          />
          <TextField
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canConfirm} onClick={handleConfirm}>
          Add Trait
        </Button>
      </DialogActions>
    </Dialog>
  );
};

